// Renders the step-up modal (see lib/stepUp.ts). Mounted once in AppShell, next
// to <ConfirmRoot />. When a gated callable returns `step-up-required`, the
// callable wrapper calls requestStepUp() and this modal runs the WebAuthn
// ceremony — enrolling a passkey first if the user has none, then authenticating
// — and settles the promise so the original call can retry (IMP-045).

import { useEffect, useState } from 'react';
import { ShieldCheck, Loader2, X, Fingerprint } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';

import { Modal } from './Modal';
import { subscribeStepUp, settleStepUp } from '../lib/stepUp';
import { useAuth } from '../lib/auth';
import { db } from '../lib/firebase';
import { defaultDeviceName, isWebauthnSupported } from '../lib/webauthnSupport';

// This component is mounted on every page (AppShell), so it must not pull the
// WebAuthn ceremony code — @simplewebauthn/browser plus the Cloud Functions
// SDK behind the callables — into the start-up bundle (IMP-062). The ceremony
// module loads when a step-up actually runs.
const loadCeremony = () => import('../lib/webauthn');

type Phase = 'checking' | 'ready' | 'busy' | 'error';

export function StepUpRoot() {
  const { user } = useAuth();
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState<Phase>('checking');
  const [needsEnroll, setNeedsEnroll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => subscribeStepUp(setActive), []);

  // On open, find out whether the user already has an enrolled credential so we
  // can label the action ("Verify" vs "Set up & verify"). Owner-readable.
  useEffect(() => {
    if (!active || !user) return;
    // Warm the ceremony module while the user reads the dialog, so the tap on
    // "Verify" goes straight into the network + passkey steps it always had.
    void loadCeremony();
    let cancelled = false;
    setPhase('checking');
    setError(null);
    void (async () => {
      try {
        const snap = await getDocs(collection(db, 'users', user.uid, 'webauthnCredentials'));
        if (cancelled) return;
        setNeedsEnroll(snap.empty);
      } catch {
        // If the lookup fails, fall back to authenticate — the server guides
        // (it returns "no security key enrolled" which surfaces as an error).
        if (!cancelled) setNeedsEnroll(false);
      } finally {
        if (!cancelled) setPhase('ready');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, user]);

  if (!active) return null;

  const cancel = () => settleStepUp(false);
  const unsupported = !isWebauthnSupported();

  const proceed = async () => {
    setPhase('busy');
    setError(null);
    try {
      const { enrollDevice, authenticateStepUp } = await loadCeremony();
      if (needsEnroll) await enrollDevice(defaultDeviceName());
      await authenticateStepUp();
      settleStepUp(true); // verified — the original call retries
    } catch (err) {
      // Most often the user dismissed the OS prompt (NotAllowedError/AbortError).
      setError(err instanceof Error ? err.message : 'Verification failed.');
      setPhase('error');
    }
  };

  return (
    <Modal onClose={cancel} variant="dialog" role="dialog" labelledBy="stepup-title">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange/10 text-orange">
            <ShieldCheck size={18} />
          </div>
          <div>
            <h2 id="stepup-title" className="text-lg font-medium text-processBlue">
              Verify it&apos;s you
            </h2>
            <p className="mt-1 text-sm text-steel">
              {unsupported
                ? 'This browser does not support device verification. Use a device with Touch ID, Windows Hello, or a passkey.'
                : needsEnroll
                  ? 'This high-impact action needs an extra check. Set up your device passkey (Touch ID, Windows Hello, or your phone) — about five seconds, once per device.'
                  : 'This high-impact action needs a quick device check (Touch ID, Windows Hello, or your passkey).'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={cancel}
          className="rounded-md p-1 text-steel hover:bg-cloud/30"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      {error && (
        <p className="rounded-lg border border-error-700/20 bg-error-50 p-2 text-sm text-error-700">
          {error}
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={cancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={unsupported || phase === 'busy' || phase === 'checking'}
          onClick={proceed}
        >
          {phase === 'busy' ? <Loader2 size={16} className="animate-spin" /> : <Fingerprint size={16} />}
          {needsEnroll ? 'Set up & verify' : 'Verify'}
        </button>
      </div>
    </Modal>
  );
}
